import { skipToken } from '@reduxjs/toolkit/dist/query';
import { shallowEqual } from 'react-redux';

import { ratingApi } from 'apis/rating';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { ConversationHeaderCsatDropdown } from './components/csatDropdown/csatDropdown';

const messagesContainType = (messages, messageType: string) =>
  messages.some(({ type }) => type === messageType);

function getCsatDisabledState(
  messages,
): 'Offered' | 'Cancelled' | 'Answered' | undefined {
  if (messagesContainType(messages, 'conversation:rated')) {
    return 'Answered';
  }
  if (messagesContainType(messages, 'conversation:rating_offered')) {
    return 'Offered';
  }
  if (messagesContainType(messages, 'conversation:rating_cancelled')) {
    return 'Cancelled';
  }
  return undefined;
}

export const ConversationHeaderCsatMenu = (): JSX.Element | null => {
  const [{ messages }, { id: csid }, asyncStatus] = useAppSelector(
    (state) => [
      state.conversation.metaConversation,
      state.conversation.metaConversation.conversation,
      state.conversation.asyncStatus,
    ],
    shallowEqual,
  );

  const {
    isCsatEligible,
    isUserExcluded,
    availableLanguages,
    isLoading: isLoadingFutureRatingInfo,
  } = ratingApi.useGetFutureRatingInfoQuery(
    csid ? { csid: csid.toString() } : skipToken,
    {
      selectFromResult: ({ data, isLoading, ...rest }) => ({
        isCsatEligible: data?.data?.csat_eligible ?? false,
        isUserExcluded: data?.data?.user_excluded_from_csat ?? false,
        availableLanguages: data?.data?.survey_languages ?? [],
        isLoading,
        ...rest,
      }),
    },
  );

  const isLoading =
    asyncStatus.getConversation === 'loading' ||
    asyncStatus.claim === 'loading' ||
    asyncStatus.close === 'loading' ||
    isLoadingFutureRatingInfo;

  // If API says CSAT is not eligible, don't show the button
  if (!isCsatEligible || isLoading) {
    return null;
  }

  const disabledText = getCsatDisabledState(messages);

  return (
    <ConversationHeaderCsatDropdown
      disabledText={disabledText}
      isLoading={isLoading}
      isUserExcluded={isUserExcluded}
      availableLanguages={availableLanguages}
    />
  );
};
